import type { ColumnsType } from 'antd/es/table';
import {
  IcCopy,
  IcEdit,
  IcImage,
  IcScreen,
  IcTrash,
  Pagination,
  Table,
  Text,
} from 'basic-ui-lib';
import { ActionProps } from 'basic-ui-lib/components/Table/Table';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMessageError } from 'services/response';
import styled from 'styled-components';
import toast from 'utils/toast';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { setPageLoading } from 'widgets/PageLoading';
import useGetPlaylistsInStore from 'modules/store/services/hooks/useGetPlaylistsInStore';
import { IPlaylist } from 'modules/playlist/interfaces/api';
import dayjs from 'dayjs';
import SetToScreenModal, { SetToScreenModalRef } from 'modules/playlist/screens/Dashboard/components/SetToScreenModal';
import EditNameModal, { EditNameModalRef } from 'basic-ui-lib/components/Modal/EditNameModal';
import playlistApis from 'modules/playlist/services/apis';

interface PlaylistTableProps {
  id: string;
}

const PlaylistTable = ({ id }: PlaylistTableProps) => {
  const modalConfirmRef = useModalConfirmRef();
  const setToScreenModalRef = useRef<SetToScreenModalRef>(null);
  const editNameModalRef = useRef<EditNameModalRef>(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const { playlistsInStore, playlistsInStoreParam, mutate } =
    useGetPlaylistsInStore(id || '');

  const columns: ColumnsType<IPlaylist> = [
    {
      title: 'NAME',
      dataIndex: 'name',
      render: (text) => (
        <Text content_2 color='primary.6'>
          {text}
        </Text>
      ),
    },
    {
      title: 'DURATION',
      dataIndex: 'duration',
      render: (text) => (
        <Text body_1 medium>
          {dayjs()
            .startOf('day')
            .add(text || 0, 'second')
            .format('HH:mm:ss')}
        </Text>
      ),
    },
    {
      title: 'ASSIGNED DEVICES',
      dataIndex: 'numberAssigned',
    },
    {
      title: 'CREATED DATE',
      dataIndex: 'createdDate',
    },
    {
      title: 'LAST UPDATED',
      dataIndex: 'lastModifiedDate',
    },
  ];

  const actions: ActionProps<IPlaylist>[] = [
    {
      label: 'Set To Screen',
      icon: <IcScreen width={16} height={16} />,
      onClick: (item) => {
        setToScreenModalRef.current?.open(item.id);
      },
    },
    {
      label: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
      onClick: (item) => {
        editNameModalRef.current?.open({
          currentName: item.name,
          id: item.id,
          onFinish: handleEditName,
        });
      },
    },
    {
      label: 'Edit Content',
      icon: <IcImage width={16} height={16} />,
      onClick: (item) => {
        navigate(`/playlist/${item.id}`);
      },
    },
    {
      label: 'Duplicate',
      icon: <IcCopy width={16} height={16} />,
      onClick: (item) => {
        console.log('Duplicate clicked', item.id);
      },
    },
    {
      label: 'Delete',
      icon: <IcTrash width={16} height={16} />,
      onClick: (item) => {
        onDeletePlaylist(item.id);
      },
    },
  ];

  const handleDeletePlaylist = async (playlistId: string) => {
    try {
      setPageLoading(true);
      const res = await playlistApis.deletePlaylist(playlistId);
      if (res?.data?.data) {
        toast.success('Playlist deleted successfully');
        mutate({
          ...playlistsInStoreParam,
        });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  };

  const onDeletePlaylist = (playlistId: string) => {
    modalConfirmRef.current?.open(() => handleDeletePlaylist(playlistId), {
      message:
        'Are you sure you want to delete this playlist? This action cannot be undone.',
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    mutate({ ...playlistsInStoreParam, page, size: 10 });
  };

  const handleEditName = async (id: string, name: string) => {
    await playlistApis.updateNamePlaylist(id, name);
    mutate({ ...playlistsInStoreParam });
  };

  return (
    <TableBox>
      <Table<IPlaylist>
        className='custom-table'
        columns={columns}
        dataSource={playlistsInStore?.content ?? []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        actions={actions}
      />
      <Pagination
        totalItems={playlistsInStore?.totalElements ?? 0}
        itemsPerPage={10}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        labelDisplay='screens'
        showRowPerPage={true}
        maxVisiblePages={3}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <SetToScreenModal ref={setToScreenModalRef} storeId={id} />
      <EditNameModal ref={editNameModalRef} />
    </TableBox>
  );
};

export default PlaylistTable;

const TableBox = styled.div`
  width: 100%;
`;
